import React from "react";
import { useParams, Link } from "react-router-dom";
import InnerHero from "../components/InnerHero";
import {
  departments,
  newsByDept,
  eventsByDept,
  clubsByDept,
} from "../data/mock";
import FacultySection from "./departments/shared/FacultySection";
import FacultyCard from "./departments/shared/FacultyCard";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Beaker,
  Users,
  Calendar,
  Microscope,
  Award,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Mail,
  Phone,
  BookOpen,
  ShieldCheck,
  PlayCircle,
  FileText,
} from "lucide-react";

const Section = ({ id, title, icon: Icon, children, sub }) => (
  <section id={id} className="scroll-mt-32">
    <div className="flex items-end justify-between gap-4 mb-5">
      <div>
        <h3
          className="text-2xl md:text-3xl text-brand font-semibold flex items-center gap-2"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {Icon && <Icon size={22} />} {title}
        </h3>
        {sub && <p className="text-sm text-[#3a3a3a]/80 italic mt-1">{sub}</p>}
      </div>
    </div>
    {children}
  </section>
);

const getAchievementSections = (achievements = []) =>
  achievements.map((achievement) => {
    if (achievement?.items) return achievement;

    if (achievement?.label || achievement?.href) {
      return {
        title: achievement.title || "Achievements",
        items: [achievement],
      };
    }

    return {
      title: "Achievements",
      items: [{ label: achievement, href: "#" }],
    };
  });

const AchievementDropdowns = ({ achievements }) => {
  const sections = getAchievementSections(achievements);

  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <details
          key={section.title}
          className="bg-white border border-brand/15 p-5"
          open
        >
          <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
            <span className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui">
              {section.title}
            </span>
            {/* <span className="text-brand text-sm">PDF</span> */}
          </summary>

          {section.items?.length > 0 ? (
            <ul className="space-y-2 mt-4">
              {section.items.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href || "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-[#2a2a2a] hover:text-brand transition"
                  >
                    <Award size={15} className="text-brand flex-shrink-0" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#3a3a3a]/70 italic mt-4">
              Content will be updated soon.
            </p>
          )}
        </details>
      ))}
    </div>
  );
};

const ProgrammeVideoSection = ({ video }) => {
  if (!video?.embedUrl) return null;

  return (
    <Section
      id={video.id || "featured-video"}
      title={video.title || "Department Video"}
      icon={PlayCircle}
      sub={video.sub}
    >
      <div className="bg-white border border-brand/15 overflow-hidden">
        <div className="aspect-video bg-black">
          <iframe
            className="w-full h-full"
            src={video.embedUrl}
            title={video.iframeTitle || video.title || "Department video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </Section>
  );
};

const SimpleTable = ({ title, columns, rows }) => (
  <div className="bg-white border border-brand/15 overflow-hidden">
    <div className="px-4 py-3 border-b border-brand/10">
      <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui">
        {title}
      </h4>
    </div>
    <table className="w-full table-fixed text-sm">
      <thead className="bg-brand text-surface">
        <tr>
          {columns.map((column, index) => (
            <th
              key={column}
              className={`text-left px-4 py-3 font-semibold align-top ${
                columns.length === 3 && index === 2 ? "w-32" : ""
              }`}
            >
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.join("-")} className="odd:bg-surface/40">
            {row.map((cell, index) => (
              <td
                key={`${cell}-${index}`}
                className="px-4 py-3 border-t border-brand/10 align-top whitespace-normal break-words"
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ProgrammeDetail = ({ programme }) => {
  const { id } = useParams();
  const [selectedClub, setSelectedClub] = React.useState(null);
  const dept = programme || departments.find((d) => d.id === id);

  if (!dept) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-24">
        <p className="text-brand text-xl">Programme not found.</p>
        <Link
          to="/academics"
          className="inline-flex items-center gap-2 text-brand underline mt-3"
        >
          <ArrowLeft size={14} /> Back to Academics
        </Link>
      </main>
    );
  }

  const facultyDeptId =
    dept.facultyDeptId ||
    {
      "industrial-electronics": "eee",
      "thermal-power-engineering": "me",
      "cse-research": "cse",
      "ece-research": "ece",
      "eee-research": "eee",
      "me-research": "me",
      physics: "bsh",
      chemistry: "bsh",
      mathematics: "bsh",
    }[dept.id] ||
    dept.id;
  const sourceFacultyData = dept.facultyData || { hod: null, members: [] };
  const localFaculty = [
    sourceFacultyData.hod,
    ...(sourceFacultyData.members || []),
  ].filter(Boolean);
  const selectedHod =
    localFaculty.find((faculty) => faculty.id === dept.hodId) || sourceFacultyData.hod;
  const hod = selectedHod ? { ...selectedHod, showPhone: true } : null;
  const facultyMembers = dept.showHodInFacultyList
    ? (sourceFacultyData.members || [])
    : localFaculty.filter((faculty) => !hod || faculty.id !== hod.id);
  const facultyData = {
    hod,
    members: facultyMembers,
  };
  const hasFacultyStrength =
    dept.facultyStrength !== undefined &&
    dept.facultyStrength !== null &&
    dept.facultyStrength !== "";
  const facultyStrength =
    hasFacultyStrength ? String(dept.facultyStrength).padStart(2, "0") : "";
  const news = newsByDept(facultyDeptId);
  const evs = eventsByDept(facultyDeptId);
  const clubs = dept.hideSharedClubs ? [] : clubsByDept(facultyDeptId);
  const departmentClubs = dept.studentActivities?.clubs || [];
  const professionalChapters = dept.studentActivities?.professionalChapters || [];
  const hasClubsSection =
    clubs.length > 0 ||
    departmentClubs.length > 0 ||
    professionalChapters.length > 0;
  const researchSupervisors = dept.supervisors || [];
  const researchScholars = dept.researchScholars || [];
  const dashboard = dept.dashboard || [];
  const academicVision = dept.academicVision || "";
  const academicOverview = dept.academicOverview || [];
  const labs = dept.labs || [];
  const curriculumSupport = dept.curriculumSupport || [];
  const programmeOutcomes = dept.programmeOutcomes || [];
  const pso = dept.pso || [];
  const peo = dept.peo || [];
  const accreditation = dept.accreditation || [];
  const achievements = dept.achievements || [];
  const facultyTable = dept.facultyTable || [];
  const teachingStaff = dept.teachingStaff || [];
  const nonTeachingStaff = dept.nonTeachingStaff || [];
  const featuredVideo = dept.featuredVideo || null;
  const programmeType = dept.programmeType || "ug";
  const showStaffProfile =
    programmeType !== "ug" &&
    (facultyTable.length > 0 || teachingStaff.length > 0 || nonTeachingStaff.length > 0);
  const overviewTitle =
    programmeType === "research"
      ? dept.name
      : dept.name;
  const relatedProgrammes = departments.filter(
    (d) => d.id !== dept.id && (d.programmeType || "ug") === programmeType
  );
  const researchAreas = Array.isArray(dept.researchAreas)
    ? dept.researchAreas
    : typeof dept.research === "string"
      ? dept.research.split(/[Â·,|]/).map((area) => area.trim()).filter(Boolean)
      : [];
  const showAcademicMeta = !dept.hideAcademicMeta && (labs.length > 0 || researchAreas.length > 0);
  const quickFacts = Array.isArray(dept.quickFacts) ? dept.quickFacts : [];
  const hasQuickFact = (label) =>
    quickFacts.some(([factLabel]) => String(factLabel).toLowerCase() === label.toLowerCase());
  const hasResearchFundedProjects =
    dept.researchFundedProjects !== undefined &&
    dept.researchFundedProjects !== null &&
    dept.researchFundedProjects !== "";
  const studentResourcePathByDept = {
    cse: "/programme/cse/student-resources",
    "cse-aiml": "/programme/cse-aiml/student-resources",
    ece: "/programme/ece/student-resources",
    eee: "/programme/eee/student-resources",
    me: "/programme/me/student-resources",
    civil: "/programme/civil/student-resources",
    bsh: "/programme/bsh/student-resources",
    physics: "/programme/bsh/physics/student-resources",
    chemistry: "/programme/bsh/chemistry/student-resources",
    maths: "/programme/bsh/maths/student-resources",
    english: "/programme/bsh/english/student-resources",
  };
  const studentResourcePath = studentResourcePathByDept[dept.id];
  const infrastructurePathByDept = {
    cse: "/programme/cse/infrastructure",
    "cse-aiml": "/programme/cse-aiml/infrastructure",
    ece: "/programme/ece/infrastructure",
    eee: "/programme/eee/infrastructure",
    me: "/programme/me/infrastructure",
    civil: "/programme/civil/infrastructure",
    physics: "/programme/bsh/physics/infrastructure",
    chemistry: "/programme/bsh/chemistry/infrastructure",
    maths: "/programme/bsh/maths/infrastructure",
    english: "/programme/bsh/english/infrastructure",
  };
  const infrastructurePath = infrastructurePathByDept[dept.id];
  const facilitiesPathByDept = {
    "industrial-electronics": "/programme/industrial-electronics/facilities",
    "thermal-power": "/programme/thermal-power-engineering/facilities",
  };
  const facilitiesPath = facilitiesPathByDept[dept.id];
  const newsEventsPath = `/programme/${dept.id}/news-events`;
  const departmentNews = dept.newsEvents?.news || [];
  const departmentEvents = dept.newsEvents?.events || [];
  const configuredReports = dept.newsEvents?.reports || dept.reports || [];
  const reportDownloads = configuredReports.length
    ? configuredReports.map((report, index) => ({
        title: report.title || report.label || `${dept.short || dept.name} Report ${index + 1}`,
        href: report.href || "#",
      }))
    : [
        {
          title: `${dept.short || dept.name} Activity Report 2025-26`,
          href: "/downloads/departments/dummy-department-report.txt",
        },
        {
          title: `${dept.short || dept.name} Workshop Report`,
          href: "/downloads/departments/dummy-department-report.txt",
        },
      ];
  const getResearchPersonFaculty = (name, facultyId) =>
    localFaculty.find((item) => item.id === facultyId) ||
    localFaculty.find((item) => item.name === name);
  const renderResearchPersonCard = ([name, role, facultyId, image]) => {
    const hideImage = image === false;
    const faculty = getResearchPersonFaculty(name, facultyId);

    if (faculty && !hideImage) {
      return (
        <FacultyCard
          key={facultyId || name}
          faculty={faculty}
          roleLabel={role}
          hideSpecializations
        />
      );
    }

    return (
      <article
        key={name}
        className="bg-white border border-brand/15 p-4 flex items-start gap-4"
      >
        {!hideImage && (
          <img
            src={image || "/dummy_image.jpeg"}
            alt={name}
            className="w-20 h-20 object-contain bg-white flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <h4 className="text-brand font-semibold leading-tight">{name}</h4>
          {role && <p className="text-xs italic text-[#3a3a3a] mt-0.5">{role}</p>}
        </div>
      </article>
    );
  };
  const normalizeClub = (club, fallbackImage, defaultCategory = "Student Club") => {
    const isString = typeof club === "string";
    const name = isString ? club : club.name;
    const category = isString ? defaultCategory : club.category || defaultCategory;
    const description = isString
      ? `Student-led technical activities, events, and peer learning through ${name}.`
      : club.description || `Student-led activities and events through ${name}.`;
    const image = isString ? fallbackImage : club.image || fallbackImage;
    const reports = isString
      ? [
          { title: `${name} Report 1`, href: "#" },
          { title: `${name} Report 2`, href: "#" },
        ]
      : (club.reports && club.reports.length > 0
          ? club.reports
          : [
              { title: `${name} Report 1`, href: "#" },
              { title: `${name} Report 2`, href: "#" },
            ]);

    return { name, category, description, image, reports };
  };
  const navItems = [
    ["overview", "Overview"],
    //dept.curriculumSupport && ["curriculum", "Curriculum"],
    // (dept.programmeOutcomes || dept.pso || dept.peo) && ["outcomes", "Outcomes"],
    //["faculty", "Faculty"],
    //["accreditation", "Accreditation"],
    // ["research", "Research"],
    achievements.length > 0 && ["achievements", "Achievements"],
    (dept.infrastructure || infrastructurePath) && ["infrastructure", "Infrastructure", infrastructurePath],
    facilitiesPath && ["facilities", "Facilities", facilitiesPath],
    showStaffProfile && ["staff-profile", "Staff"],
    ["news", "News & Events", newsEventsPath],
    // studentResourcePath && ["student-resources", "student_resources", studentResourcePath],
    hasClubsSection && ["clubs", "Clubs"],
  ].filter(Boolean);

  return (
    <main>
      <InnerHero
        title={dept.name}
        image={dept.image}
        breadcrumbs={[
          { label: "Academics", to: "/academics" },
          { label: "Departments", to: "/academics#departments" },
          { label: dept.short },
        ]}
      />

      <section className="bg-surface-alt/60 border-b border-brand/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap gap-x-6 gap-y-2 text-xs font-sans-ui font-semibold tracking-wider text-brand">
          {navItems.map(([h, l, to]) =>
            to ? (
              <Link key={h} to={to} className="uppercase hover:underline">
                {l}
              </Link>
            ) : (
              <a key={h} href={`#${h}`} className="uppercase hover:underline">
                {l}
              </a>
            )
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-12">
            <Section id="overview" title={overviewTitle}>
              <p className="text-[#2a2a2a]/85 leading-relaxed text-[15.5px] mt-3">
                {dept.description}
              </p>
              {dashboard.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                  {dashboard.map(([label, value]) => (
                    <div
                      key={label}
                      className="bg-[#7a1d2c] p-4 border border-[#7a1d2c]"
                    >
                      <div
                        className="text-3xl text-white font-semibold"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        {value}
                      </div>

                      <div className="text-xs uppercase text-white/80 font-sans-ui tracking-widest mt-1">
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {academicOverview.length > 0 && (
                <>
                  {academicVision && (
                    <div className="bg-white border border-brand/15 p-4 mt-5 flex items-start gap-3 text-left">
                      <ShieldCheck size={18} className="text-brand flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-brand font-semibold">Vision</h4>
                        <p className="text-sm text-[#2a2a2a]/85 leading-relaxed mt-1 text-justify">
                          {academicVision}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="grid md:grid-cols-3 gap-3 mt-5">
                    {academicOverview.map((item) => (
                      <div key={item.title} className="bg-white p-4 border border-brand/15 text-left">
                        <h4 className="text-brand font-semibold text-left">{item.title}</h4>
                        {Array.isArray(item.text) ? (
                          <ul className="list-disc pl-5 text-sm text-[#2a2a2a]/85 mt-2 leading-relaxed text-left space-y-1">
                            {item.text.map((point) => (
                              <li key={point}>{point}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-[#2a2a2a]/85 mt-2 leading-relaxed text-left">{item.text}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
              {showAcademicMeta && (
                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  {labs.length > 0 && (
                    <div className="bg-white p-4 border border-brand/15">
                      <p className="text-xs uppercase text-[#3a3a3a]/70 font-sans-ui tracking-widest">
                        Laboratories
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {labs.map((l) => (
                          <span
                            key={l}
                            className="px-2.5 py-1 bg-brand/10 text-brand text-xs rounded-full"
                          >
                            {l}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {researchAreas.length > 0 && (
                    <div className="bg-white p-4 border border-brand/15">
                      <p className="text-xs uppercase text-[#3a3a3a]/70 font-sans-ui tracking-widest">
                        Research Areas
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {researchAreas.map((area) => (
                          <span
                            key={area}
                            className="px-2.5 py-1 bg-brand/10 text-brand text-xs rounded-full"
                          >
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Section>

            {curriculumSupport.length > 0 && (
              <Section id="curriculum" title="Curriculum Support" icon={BookOpen}>
                <ul className="space-y-2">
                  {curriculumSupport.map((item) => (
                    <li key={item} className="flex items-start gap-3 bg-white p-4 border border-brand/15">
                      <CheckCircle2 size={18} className="text-brand flex-shrink-0 mt-0.5" />
                      <span className="text-[15px] text-[#2a2a2a]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {(programmeOutcomes.length > 0 || pso.length > 0 || peo.length > 0) && (
              <Section id="outcomes" title="Outcome-Based Education" icon={Sparkles}>
                {programmeOutcomes.length > 0 && (
                  <div className="bg-white border border-brand/15 p-5">
                    <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
                      Programme Outcomes
                    </h4>
                    <ol className="space-y-2 text-[15px] text-[#2a2a2a]/85">
                      {programmeOutcomes.map((item, idx) => (
                        <li key={item}>
                          <span className="text-brand font-semibold">PO{idx + 1}:</span> {item}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  {pso.length > 0 && (
                    <div className="bg-white border border-brand/15 p-5">
                      <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
                        Programme Specific Outcomes
                      </h4>
                      <ul className="space-y-2 text-[15px] text-[#2a2a2a]/85">
                        {pso.map((item, idx) => (
                          <li key={item}>
                            <span className="text-brand font-semibold">PSO{idx + 1}:</span> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {peo.length > 0 && (
                    <div className="bg-white border border-brand/15 p-5">
                      <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
                        Programme Educational Objectives
                      </h4>
                      <ul className="space-y-2 text-[15px] text-[#2a2a2a]/85">
                        {peo.map((item, idx) => (
                          <li key={item}>
                            <span className="text-brand font-semibold">PEO{idx + 1}:</span> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </Section>
            )}

            <ProgrammeVideoSection video={featuredVideo} />

            {!dept.hideFacultySection && (
              <FacultySection
                facultyData={facultyData}
                programmeShort={dept.short}
                Section={Section}
              />
            )}

            {(researchSupervisors.length > 0 || researchScholars.length > 0) && (
              <Section id="research-people" title="Research" icon={Users}>
                {researchSupervisors.length > 0 && (
                  <div>
                    <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
                      Research Supervisors
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {researchSupervisors.map(renderResearchPersonCard)}
                    </div>
                  </div>
                )}

                {researchScholars.length > 0 && (
                  <div className={researchSupervisors.length > 0 ? "mt-6" : ""}>
                    <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
                      Research Scholars
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {researchScholars.map(renderResearchPersonCard)}
                    </div>
                  </div>
                )}
              </Section>
            )}

            {/* Accreditation */}
            {accreditation.length > 0 && (
            <Section id="accreditation" title="Accreditation & Approvals" icon={CheckCircle2}>
              <ul className="space-y-2">
                {accreditation.map((a) => (
                  <li
                    key={a}
                    className="flex items-start gap-3 bg-white p-4 border-l-4 border-brand"
                  >
                    <CheckCircle2 size={18} className="text-brand flex-shrink-0 mt-0.5" />
                    <span className="text-[15px] text-[#2a2a2a]">{a}</span>
                  </li>
                ))}
              </ul>
            </Section>
            )}

            {/* Research */}
            {/*<Section id="research" title="Research & Funded Projects" icon={Microscope}>
              <div className="space-y-3">
                {dept.projects.map((p) => (
                  <div
                    key={p.title}
                    className="bg-white p-5 border border-brand/15"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h4 className="text-brand font-semibold">{p.title}</h4>
                      <span className="text-xs font-sans-ui bg-brand/10 text-brand px-2 py-1 rounded-full">
                        {p.amount}
                      </span>
                    </div>
                    <p className="text-sm text-[#3a3a3a]/85 mt-1">
                      Funding agency: <span className="italic">{p.funding}</span>
                    </p>
                  </div>
                ))}
              </div>
              <Link
                to="/research"
                className="inline-flex items-center gap-1 text-brand hover:underline mt-5 text-sm font-sans-ui"
              >
                Explore all research at VDIT <ArrowRight size={14} />
              </Link>
            </Section>*/} 

            {/* Achievements */}
            {achievements.length > 0 && (
            <Section id="achievements" title="Achievements" icon={Sparkles}>
              <AchievementDropdowns achievements={achievements} />
            </Section>
            )}
            
            {/* {(dept.infrastructure || dept.infrastructureTable || dept.infrastructureTables) && (
              <Section id="infrastructure" title="Department Infrastructure" icon={Beaker}>
                {dept.infrastructureImages?.length > 0 && (
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    {dept.infrastructureImages.map((image) => (
                      <figure
                        key={image.src}
                        className="bg-white border border-brand/15 overflow-hidden"
                      >
                        <div className="aspect-[16/10] overflow-hidden">
                          <img
                            src={image.src}
                            alt={image.alt || image.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <figcaption className="px-4 py-3">
                          <span className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui">
                            {image.title}
                          </span>
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                )}
                {dept.infrastructureTables ? (
                  <div className="space-y-4">
                    {dept.infrastructureTables.map((table, tableIndex) => (
                      <div key={tableIndex} className="overflow-x-auto bg-white border border-brand/15">
                        <table className="w-full min-w-[720px] text-sm">
                          <thead className="bg-brand text-surface">
                            <tr>
                              {table.columns.map((column) => (
                                <th
                                  key={column}
                                  className="text-left px-4 py-3 font-semibold uppercase tracking-widest text-xs"
                                >
                                  {column}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {table.rows.map((row, rowIndex) => (
                              <tr key={rowIndex} className="odd:bg-surface/40">
                                {row.map((cell, cellIndex) => (
                                  <td
                                    key={`${rowIndex}-${cellIndex}`}
                                    className="px-4 py-3 border-t border-brand/10 align-top whitespace-normal break-words text-[#2a2a2a]"
                                  >
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                ) : dept.infrastructureTable ? (
                  <div className="overflow-x-auto bg-white border border-brand/15">
                    <table className="w-full min-w-[720px] text-sm">
                      <thead className="bg-brand text-surface">
                        <tr>
                          {dept.infrastructureTable.columns.map((column) => (
                            <th
                              key={column}
                              className="text-left px-4 py-3 font-semibold uppercase tracking-widest text-xs"
                            >
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dept.infrastructureTable.rows.map((row, rowIndex) => (
                          <tr key={rowIndex} className="odd:bg-surface/40">
                            {row.map((cell, cellIndex) => (
                              <td
                                key={`${rowIndex}-${cellIndex}`}
                                className="px-4 py-3 border-t border-brand/10 align-top whitespace-normal break-words text-[#2a2a2a]"
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="overflow-x-auto bg-white border border-brand/15">
                    <table className="w-full min-w-[520px] text-sm">
                      <thead className="bg-brand text-surface">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold uppercase tracking-widest text-xs">
                            Infrastructure Item
                          </th>
                          <th className="text-left px-4 py-3 font-semibold uppercase tracking-widest text-xs">
                            Details
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dept.infrastructure.map(([label, value]) => (
                          <tr key={label} className="odd:bg-surface/40">
                            <td className="px-4 py-3 border-t border-brand/10 align-top font-medium text-[#2a2a2a]">
                              {label}
                            </td>
                            <td className="px-4 py-3 border-t border-brand/10 align-top whitespace-normal break-words text-[#2a2a2a]">
                              {value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            )} */}

            {showStaffProfile && (
              <Section id="staff-profile" title="Staff Profile" icon={Users}>
                {facultyTable.length > 0 && (
                  <SimpleTable
                    title="Faculty Qualifications and Experience"
                    columns={["Faculty Name", "Qualifications", "Academic Experience"]}
                    rows={facultyTable}
                  />
                )}
                <div className="grid lg:grid-cols-2 gap-4 mt-4">
                  {teachingStaff.length > 0 && (
                    <SimpleTable
                      title="Teaching Staff"
                      columns={["Staff Name", "Designation"]}
                      rows={teachingStaff}
                    />
                  )}
                  {nonTeachingStaff.length > 0 && (
                    <SimpleTable
                      title="Non-Teaching Staff"
                      columns={["Staff Name", "Designation"]}
                      rows={nonTeachingStaff}
                    />
                  )}
                </div>
              </Section>
            )}

            {/* <Section id="news" title="News & Events" icon={Calendar}>
              <div className="bg-white border border-brand/15 p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="text-sm text-[#2a2a2a]/80 leading-relaxed">
                      View department updates, events, and downloadable reports on a dedicated page.
                    </p>
                    <p className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mt-3">
                      {departmentNews.length} News | {departmentEvents.length} Events | {reportDownloads.length} Reports
                    </p>
                  </div>
                  <Link
                    to={newsEventsPath}
                    className="inline-flex items-center justify-center gap-2 bg-brand text-white px-4 py-2 text-sm font-semibold hover:bg-brand-dark transition"
                  >
                    Open Page <ArrowRight size={16} />
                  </Link>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  {reportDownloads.map((report) => (
                    <a
                      key={report.title}
                      href={report.href}
                      download
                      className="flex items-center gap-3 border border-brand/10 p-3 text-sm text-[#2a2a2a] hover:text-brand hover:border-brand/30 transition"
                    >
                      <FileText size={16} className="text-brand flex-shrink-0" />
                      {report.title}
                    </a>
                  ))}
                </div>
              </div>
            </Section> */}

            {/* Clubs */}
            {hasClubsSection && (
              <Section id="clubs" title="Student Clubs" icon={Users}>
                <div className="grid sm:grid-cols-2 gap-4">
                  {clubs.map((c) => (
                    <button
                      type="button"
                      key={c.id || c.name}
                      onClick={() => setSelectedClub(normalizeClub(c, c.image || dept.image, c.category || "Student Club"))}
                      className="bg-white border border-brand/15 overflow-hidden card-hover block text-left"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="p-4">
                        <span className="text-xs uppercase tracking-widest text-brand font-sans-ui">
                          {c.category}
                        </span>
                        <h4 className="text-brand font-semibold mt-0.5 leading-snug">{c.name}</h4>
                        <p className="text-xs text-[#3a3a3a]/85 mt-1.5 line-clamp-2">{c.description}</p>
                      </div>
                    </button>
                  ))}
                  {departmentClubs.map((club) => {
                    const normalizedClub = normalizeClub(club, clubs[0]?.image || dept.image);

                    return (
                      <button
                        type="button"
                        key={normalizedClub.name}
                        onClick={() => setSelectedClub(normalizedClub)}
                        className="bg-white border border-brand/15 overflow-hidden card-hover block text-left"
                      >
                        <div className="aspect-[16/9] overflow-hidden">
                          <img
                            src={normalizedClub.image}
                            alt={normalizedClub.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-4">
                          <span className="text-xs uppercase tracking-widest text-brand font-sans-ui">
                            {normalizedClub.category}
                          </span>
                          <h4 className="text-brand font-semibold mt-0.5 leading-snug">
                            {normalizedClub.name}
                          </h4>
                          <p className="text-xs text-[#3a3a3a]/85 mt-1.5 line-clamp-2">
                            {normalizedClub.description}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                  {professionalChapters.map((chapter) => {
                    const normalizedChapter = normalizeClub(
                      chapter,
                      clubs[0]?.image || dept.image,
                      "Professional Chapter"
                    );

                    return (
                      <button
                        type="button"
                        key={normalizedChapter.name}
                        onClick={() => setSelectedClub(normalizedChapter)}
                        className="bg-white border border-brand/15 overflow-hidden card-hover block text-left"
                      >
                        <div className="aspect-[16/9] overflow-hidden">
                          <img
                            src={normalizedChapter.image}
                            alt={normalizedChapter.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-4">
                          <span className="text-xs uppercase tracking-widest text-brand font-sans-ui">
                            {normalizedChapter.category}
                          </span>
                          <h4 className="text-brand font-semibold mt-0.5 leading-snug">
                            {normalizedChapter.name}
                          </h4>
                          <p className="text-xs text-[#3a3a3a]/85 mt-1.5 line-clamp-2">
                            {normalizedChapter.description}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Section>
            )}
          </div>

          <aside className="space-y-5">
            <div className="bg-white border border-brand/15 p-5 lg:sticky lg:top-36 self-start">
              <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui">
                Quick Facts
              </h4>
              <div className="mt-3 space-y-3 text-sm">
                {quickFacts.length > 0 ? (
                  quickFacts.map(([label, value]) => (
                    <div key={label}>
                      {label}: <strong>{value || "To be updated"}</strong>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-brand" />
                      <span>Established: <strong>{dept.established}</strong></span>
                    </div>
                    <div>Duration: <strong>{dept.duration || "4 Years (8 Semesters)"}</strong></div>
                    <div>Affiliation: <strong>VTU, Belagavi</strong></div>
                  </>
                )}
                {/* <div className="flex items-center gap-2">
                  <Users size={14} className="text-brand" />
                  <span>
                    {programmeType === "research" ? "Research Intake" : "Annual Intake"}:{" "}
                    <strong>{dept.intake ? `${dept.intake} seats` : "To be updated"}</strong>
                  </span>
                </div> */}
                
                {hasFacultyStrength && (
                  <div>Faculty Strength: <strong>{facultyStrength}</strong></div>
                )}
                {hasResearchFundedProjects && !hasQuickFact("Research Funded Projects") && (
                  <div>
                    Research Funded Projects: <strong>{dept.researchFundedProjects}</strong>
                  </div>
                )}
              </div>
              {/* {programmeType !== "research" && (
                <Link
                  to="/admissions"
                  className="block mt-5 text-center bg-brand text-surface py-2.5 text-sm font-semibold font-sans-ui hover:bg-brand-dark transition"
                >
                  Apply for {dept.short}
                </Link>
              )} */}

              <div className="mt-6 pt-5 border-t border-brand/10">
                <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
                  Other {programmeType === "research" ? "Research Centres" : "Programmes"}
                </h4>
                <ul className="space-y-1.5">
                  {relatedProgrammes
                    .map((d) => (
                      <li key={d.id}>
                        <Link
                          to={`/programme/${d.id}`}
                          className="text-sm text-[#2a2a2a] hover:text-brand flex items-center gap-1"
                        >
                          <Beaker size={12} className="text-brand" />{d.short}-{d.name.replace(/\s*\([^)]*\)/g, "")}
                        </Link>
                      </li>
                    ))}
                </ul>
                {studentResourcePath && (
                  <Link
                    to={studentResourcePath}
                    className="mt-5 flex items-center justify-center gap-2 bg-brand text-surface px-4 py-2.5 text-sm font-semibold font-sans-ui hover:bg-brand-dark transition"
                  >
                    <BookOpen size={15} />
                    Student Resources
                  </Link>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Dialog open={Boolean(selectedClub)} onOpenChange={(open) => !open && setSelectedClub(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedClub && (
            <>
              <DialogHeader>
                <DialogTitle className="text-brand text-2xl font-semibold">
                  {selectedClub.name}
                </DialogTitle>
                <DialogDescription className="text-[#3a3a3a]">
                  {selectedClub.category}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <img
                  src={selectedClub.image}
                  alt={selectedClub.name}
                  className="w-full aspect-[16/9] object-cover border border-brand/15"
                />
                <p className="text-sm text-[#2a2a2a]/85 leading-relaxed">
                  {selectedClub.description}
                </p>

                <div>
                  <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
                    Reports
                  </h4>
                  <div className="space-y-2">
                    {selectedClub.reports.map((report) => (
                      <a
                        key={report.title}
                        href={report.href}
                        target={report.href === "#" ? undefined : "_blank"}
                        rel={report.href === "#" ? undefined : "noreferrer"}
                        className="flex items-center gap-3 bg-surface/60 border border-brand/10 px-4 py-3 text-sm text-[#2a2a2a] hover:text-brand transition"
                      >
                        <FileText size={16} className="text-brand flex-shrink-0" />
                        <span>{report.title}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default ProgrammeDetail;
